import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Youtube, ArrowRight, ExternalLink, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { VIDEOS, getVideoBySlug, embedUrl, watchUrl, SERVICES_MAP, CYBER_GUY_CHANNEL_URL } from '../data/videos';
import YouTubeVideoCard from '../components/YouTubeVideoCard';
import SEO, { SITE, breadcrumb, orgRef } from '../components/SEO';

const VideoDetailPage = () => {
  const { slug } = useParams();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (typeof document !== 'undefined') {
      const v = getVideoBySlug(slug);
      if (v) document.title = `${v.title} | The Cyber Guy`;
    }
  }, [slug]);

  const video = getVideoBySlug(slug);
  if (!video) return <Navigate to="/the-cyber-guy" replace />;

  const related = VIDEOS.filter(
    (v) => v.youtubeId !== video.youtubeId && (v.category === video.category || v.service === video.service)
  ).slice(0, 3);

  const serviceSlug = SERVICES_MAP[video.service];

  return (
    <div className="min-h-screen bg-[#06101F] pt-16 sm:pt-20">
      <SEO
        path={`/the-cyber-guy/${video.slug}`}
        title={`${video.title} | The Cyber Guy`}
        description={video.description}
        image={video.thumbnailUrl}
        jsonLd={[
          breadcrumb([
            { name: 'Home', path: '/' },
            { name: 'The Cyber Guy', path: '/the-cyber-guy' },
            { name: video.title, path: `/the-cyber-guy/${video.slug}` },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'VideoObject',
            name: video.title,
            description: video.description,
            thumbnailUrl: video.thumbnailUrl,
            contentUrl: video.youtubeUrl,
            embedUrl: video.embedUrl,
            publisher: orgRef,
            url: `${SITE}/the-cyber-guy/${video.slug}`,
            genre: video.category,
          },
        ]}
      />

      {/* Header — dark (media context) */}
      <section className="relative overflow-hidden bg-[#06101F] text-white">
        <div className="absolute inset-0 bg-kv-grid opacity-50" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-8">
          <Link
            to="/the-cyber-guy"
            className="text-[11px] uppercase tracking-[0.22em] text-cyan-300/90 mb-4 inline-block hover:text-cyan-200 font-semibold"
          >
            &larr; All Cyber Guy Videos
          </Link>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <span className="px-3 py-1 bg-cyan-500/10 border border-cyan-400/30 text-cyan-300 rounded-full text-[11px] uppercase tracking-[0.2em] font-semibold">
              {video.category}
            </span>
            {serviceSlug && (
              <Link
                to={`/services/${serviceSlug}`}
                className="px-3 py-1 bg-white/[0.04] border border-white/15 text-slate-200 rounded-full text-[11px] uppercase tracking-[0.2em] font-semibold hover:border-cyan-400/50 hover:text-cyan-300 transition-colors"
              >
                Related Service: {video.service}
              </Link>
            )}
          </div>
          <h1 className="mt-5 font-editorial text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-[1.1]">
            {video.title}
          </h1>
          {video.description && (
            <p className="mt-4 text-base sm:text-lg text-slate-300/95 leading-relaxed max-w-3xl">
              {video.description}
            </p>
          )}
        </div>
      </section>

      {/* Player — dark */}
      <section className="bg-[#06101F] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-14">
          <div className="relative w-full aspect-video bg-slate-900 rounded-2xl overflow-hidden ring-1 ring-white/10">
            <iframe
              src={embedUrl(video.youtubeId)}
              title={video.title}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="video-detail-iframe"
            />
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <a href={watchUrl(video.youtubeId)} target="_blank" rel="noopener noreferrer" data-testid="video-detail-watch-yt">
              <Button className="w-full sm:w-auto px-6 py-5 bg-white text-[#06101F] hover:bg-cyan-100 rounded-full font-semibold">
                <Youtube className="w-4 h-4 mr-2" /> Watch on YouTube
                <ExternalLink className="w-3.5 h-3.5 ml-2" />
              </Button>
            </a>
            {serviceSlug && (
              <Link to={`/services/${serviceSlug}`}>
                <Button
                  variant="outline"
                  data-testid="video-detail-service"
                  className="w-full sm:w-auto px-6 py-5 bg-transparent border-cyan-300/50 text-cyan-100 hover:bg-white/5 hover:text-white rounded-full"
                >
                  Learn about {video.service}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Business CTA — WHITE (Kennyvicops mode) */}
      <section className="relative bg-white bg-kv-grid-light text-[#101828]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20 sm:py-24 text-center">
          <div className="inline-flex items-center gap-2.5 mb-4">
            <span className="w-6 h-px bg-blue-600/70" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-blue-700 font-semibold">From Education to Action</span>
          </div>
          <h2 className="font-editorial text-3xl sm:text-4xl font-semibold text-[#06101F] leading-[1.1]">
            Need help applying this to your business?
          </h2>
          <p className="mt-4 text-[#475467] text-base sm:text-lg leading-relaxed">
            Kennyvicops helps businesses turn cybersecurity education into practical action.
          </p>
          <div className="mt-8">
            <Link to="/contact">
              <Button
                data-testid="video-detail-consultation"
                className="px-8 py-6 bg-[#06101F] hover:bg-[#0B1B35] text-white rounded-full font-semibold"
              >
                Request a Security Consultation <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Enjoyed this? — dark */}
      <section className="relative overflow-hidden bg-[#06101F] text-white border-t border-white/5">
        <div className="absolute inset-0 bg-kv-grid opacity-50" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-24 text-center">
          <div className="inline-flex items-center gap-2.5 mb-4">
            <span className="w-6 h-px bg-cyan-300/70" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-cyan-300/90 font-semibold">Enjoyed This Video?</span>
          </div>
          <h2 className="font-editorial text-2xl sm:text-4xl font-semibold text-white leading-[1.1]">
            Watch more simple, practical and actionable cybersecurity content.
          </h2>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a href={CYBER_GUY_CHANNEL_URL} target="_blank" rel="noopener noreferrer">
              <Button
                data-testid="video-detail-watch-more"
                className="w-full sm:w-auto px-7 py-5 bg-white text-[#06101F] hover:bg-cyan-100 rounded-full font-semibold"
              >
                <Youtube className="w-4 h-4 mr-2" /> Watch More on YouTube
              </Button>
            </a>
            <a href={CYBER_GUY_CHANNEL_URL} target="_blank" rel="noopener noreferrer">
              <Button
                variant="outline"
                data-testid="video-detail-subscribe"
                className="w-full sm:w-auto px-7 py-5 bg-transparent border-cyan-300/50 text-cyan-100 hover:bg-white/5 hover:text-white rounded-full"
              >
                Subscribe on YouTube
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Related videos — dark */}
      {related.length > 0 && (
        <section className="relative bg-[#06101F] text-white border-t border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-10">
              <div>
                <div className="inline-flex items-center gap-2.5 mb-3">
                  <span className="w-6 h-px bg-cyan-300/70" />
                  <span className="text-[11px] uppercase tracking-[0.22em] text-cyan-300/90 font-semibold">Related Videos</span>
                </div>
                <h2 className="font-editorial text-2xl sm:text-4xl font-semibold text-white leading-[1.1]">Keep learning</h2>
              </div>
              <Link to="/the-cyber-guy" className="text-sm text-cyan-300 hover:text-cyan-200">
                View all Cyber Guy videos &rarr;
              </Link>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((v) => (<YouTubeVideoCard key={v.youtubeId} video={v} />))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default VideoDetailPage;
